import React from "react";
import { 
  Trash2, 
  Copy, 
  Edit, 
  Save, 
  X, 
  PlusCircle,
  Image as ImageIcon
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import AutocompleteInput from "@/components/common/AutocompleteInput";
import { toast } from "sonner";

/**
 * Factory Manual Add Modal
 * Modal for adding a new factory cost type manually with image, dimensions, and price
 */
export const FactoryManualAddModal = ({
  isOpen,
  onClose,
  manualItem,
  setManualItem,
  onSave,
  availableFactories = [],
  allTypeNames = [],
  allProjects = []
}) => {
  const [dragActive, setDragActive] = React.useState(false);

  const handleImageFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setManualItem(prev => ({ ...prev, image_data: reader.result }));
      toast.success('Imagen cargada correctamente');
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleImageFile(files[0]);
    }
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (items) {
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          e.preventDefault();
          const blob = items[i].getAsFile();
          handleImageFile(blob);
          break;
        }
      }
    }
  };

  // Calculate preview
  const previewArea = manualItem.width && manualItem.height 
    ? parseFloat(manualItem.width) * parseFloat(manualItem.height) 
    : 0;
  const previewCostPerM2 = previewArea > 0 && manualItem.cost 
    ? parseFloat(manualItem.cost) / previewArea 
    : 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PlusCircle className="w-5 h-5" />
            Agregar Costo de Fábrica Manual
          </DialogTitle>
          <DialogDescription>
            Crea un nuevo tipo de costo manualmente con imagen, dimensiones y precio
          </DialogDescription>
        </DialogHeader>
        
        <div 
          className="space-y-4"
          onPaste={handlePaste}
          tabIndex={0}
        >
          {/* Image Drop Zone */}
          <div
            className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
              dragActive ? 'border-blue-500 bg-blue-50' : 'border-slate-300 hover:border-slate-400'
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
            onClick={() => document.getElementById('factory-modal-image-input').click()}
          >
            <input
              id="factory-modal-image-input"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => e.target.files[0] && handleImageFile(e.target.files[0])}
            />
            
            {manualItem.image_data ? (
              <div className="relative inline-block">
                <img 
                  src={manualItem.image_data} 
                  alt="Preview" 
                  className="max-h-32 rounded-lg mx-auto"
                />
                <Button
                  variant="destructive"
                  size="sm"
                  className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    setManualItem(prev => ({ ...prev, image_data: null }));
                  }}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ) : (
              <div className="py-4">
                <ImageIcon className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                <p className="text-sm text-slate-600">Arrastra, pega (Ctrl+V) o haz clic</p>
                <p className="text-xs text-slate-400 mt-1">Haz clic aquí y luego Ctrl+V para pegar</p>
              </div>
            )}
          </div>

          {/* Name with autocomplete */}
          <div>
            <Label className="text-sm font-medium">Nombre del Tipo *</Label>
            <AutocompleteInput
              data-testid="factory-manual-name-input"
              value={manualItem.name}
              onChange={(e) => setManualItem(prev => ({ ...prev, name: e.target.value }))}
              suggestions={allTypeNames}
              placeholder="Ej: Puerta Corrediza 2 Hojas"
              className="mt-1"
              maxSuggestions={15}
              minCharsToShow={1}
            />
          </div>

          {/* Dimensions and Cost */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-sm font-medium">Ancho (m) *</Label>
              <Input
                type="number"
                step="0.001"
                data-testid="factory-manual-width-input"
                value={manualItem.width}
                onChange={(e) => setManualItem(prev => ({ ...prev, width: e.target.value }))}
                placeholder="1.5"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Alto (m) *</Label>
              <Input
                type="number"
                step="0.001"
                data-testid="factory-manual-height-input"
                value={manualItem.height}
                onChange={(e) => setManualItem(prev => ({ ...prev, height: e.target.value }))}
                placeholder="2.1"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Costo USD *</Label>
              <Input
                type="number"
                step="0.01"
                data-testid="factory-manual-cost-input"
                value={manualItem.cost}
                onChange={(e) => setManualItem(prev => ({ ...prev, cost: e.target.value }))}
                placeholder="250.00"
                className="mt-1"
              />
            </div>
          </div>

          {/* Calculated values */}
          {previewArea > 0 && previewCostPerM2 > 0 && (
            <div className="bg-slate-50 rounded-lg p-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Área:</span>
                <span className="font-medium">{previewArea.toFixed(3)} m²</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-slate-600">Costo/m²:</span>
                <span className="font-medium text-green-600">${previewCostPerM2.toFixed(2)}</span>
              </div>
            </div>
          )}

          {/* Material and Color */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-medium">Material</Label>
              <Select 
                value={manualItem.material} 
                onValueChange={(v) => setManualItem(prev => ({ ...prev, material: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Aluminio">Aluminio</SelectItem>
                  <SelectItem value="uPVC">uPVC</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium">Color</Label>
              <Input
                value={manualItem.color}
                onChange={(e) => setManualItem(prev => ({ ...prev, color: e.target.value }))}
                placeholder="Blanco"
                className="mt-1"
              />
            </div>
          </div>

          {/* Project Name */}
          <div>
            <Label className="text-sm font-medium">Proyecto *</Label>
            <div className="relative mt-1">
              <Input
                value={manualItem.project_name}
                onChange={(e) => setManualItem(prev => ({ ...prev, project_name: e.target.value }))}
                placeholder="Ej: CASA GONZALEZ, EDIFICIO CENTRO"
                list="factory-manual-projects"
              />
              <datalist id="factory-manual-projects">
                {allProjects.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
            </div>
          </div>

          {/* Factory Name */}
          <div>
            <Label className="text-sm font-medium">Fábrica/Proveedor</Label>
            <div className="relative mt-1">
              <Input
                value={manualItem.factory_name}
                onChange={(e) => setManualItem(prev => ({ ...prev, factory_name: e.target.value }))}
                placeholder="Ej: CONCH, ACACIAS"
                list="factory-manual-factories"
              />
              <datalist id="factory-manual-factories">
                {availableFactories.map((f) => (
                  <option key={f} value={f} />
                ))}
              </datalist>
            </div>
          </div>
          
          {/* Extra Specs / Varios */}
          <div>
            <Label className="text-sm font-medium">Varios (características especiales)</Label>
            <Input
              value={manualItem.extra_specs || ""}
              onChange={(e) => setManualItem(prev => ({ ...prev, extra_specs: e.target.value }))}
              placeholder="Ej: Con mosquitero, Doble hoja, etc."
              className="mt-1"
              data-testid="factory-manual-extras-input"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onClose(false)}>
              Cancelar
            </Button>
            <Button 
              data-testid="factory-manual-save-btn"
              onClick={onSave}
              disabled={!manualItem.name || !manualItem.width || !manualItem.height || !manualItem.cost || !manualItem.project_name}
            >
              <Save className="w-4 h-4 mr-2" />
              Guardar Tipo
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Factory Edit Modal
 * Modal for editing an existing factory cost type
 */
export const FactoryEditModal = ({
  isOpen,
  onClose,
  editType,
  setEditType,
  onSave,
  availableFactories = []
}) => {
  if (!editType) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="w-5 h-5" />
            Editar Tipo de Fábrica
          </DialogTitle>
          <DialogDescription>
            Modifica los datos del tipo seleccionado
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Image preview */}
          {editType.image_data && (
            <div className="flex justify-center">
              <img 
                src={editType.image_data} 
                alt={editType.name}
                className="max-h-24 rounded-lg border"
              />
            </div>
          )}

          <div>
            <Label>Nombre</Label>
            <Input
              data-testid="factory-edit-name-input"
              value={editType.name || ""}
              onChange={(e) => setEditType(prev => ({ ...prev, name: e.target.value }))}
              className="mt-1"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Material</Label>
              <Select 
                value={editType.material || "Aluminio"} 
                onValueChange={(v) => setEditType(prev => ({ ...prev, material: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Aluminio">Aluminio</SelectItem>
                  <SelectItem value="uPVC">uPVC</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Color</Label>
              <Input
                value={editType.color || ""}
                onChange={(e) => setEditType(prev => ({ ...prev, color: e.target.value }))}
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label>Fábrica/Proveedor</Label>
            <Input
              value={editType.factory_name || ""}
              onChange={(e) => setEditType(prev => ({ ...prev, factory_name: e.target.value }))}
              className="mt-1"
              list="edit-factory-list"
            />
            <datalist id="edit-factory-list">
              {availableFactories.map((f) => (
                <option key={f} value={f} />
              ))}
            </datalist>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Serie</Label>
              <Input
                value={editType.series || ""}
                onChange={(e) => setEditType(prev => ({ ...prev, series: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Vidrio</Label>
              <Input
                value={editType.glass_spec || ""}
                onChange={(e) => setEditType(prev => ({ ...prev, glass_spec: e.target.value }))}
                className="mt-1"
              />
            </div>
          </div>
          
          <div>
            <Label>Varios (características especiales)</Label>
            <Input
              value={editType.extra_specs || ""}
              onChange={(e) => setEditType(prev => ({ ...prev, extra_specs: e.target.value }))}
              className="mt-1"
              placeholder="Ej: Con mosquitero, Doble hoja, etc."
              data-testid="factory-edit-extras-input"
            />
          </div>

          {/* Stats (read-only) */}
          <div className="bg-slate-50 rounded-lg p-3 text-sm">
            <div className="grid grid-cols-3 gap-2">
              <div>
                <span className="text-slate-500">Muestras:</span>
                <span className="ml-1 font-medium">{editType.entries_count}</span>
              </div>
              <div>
                <span className="text-slate-500">Área:</span>
                <span className="ml-1 font-medium">{editType.total_area?.toFixed(2)} m²</span>
              </div>
              <div>
                <span className="text-slate-500">Prom:</span>
                <span className="ml-1 font-medium text-green-600">${editType.average_cost_per_m2?.toFixed(2)}/m²</span>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onClose(false)}>
              Cancelar
            </Button>
            <Button onClick={() => onSave()} data-testid="factory-edit-save-btn">
              <Save className="w-4 h-4 mr-2" />
              Guardar Cambios
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Factory Delete Modal
 * Confirmation modal for deleting a factory cost type
 */
export const FactoryDeleteModal = ({
  isOpen,
  onClose,
  deleteType,
  onConfirm
}) => {
  if (!deleteType) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Trash2 className="w-5 h-5" />
            Confirmar Eliminación
          </DialogTitle>
          <DialogDescription>
            Esta acción no se puede deshacer
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800">
              ¿Estás seguro de que deseas eliminar el tipo <strong>{deleteType.name}</strong>?
            </p>
            <p className="text-sm text-red-600 mt-2">
              Se eliminarán {deleteType.entries_count} registros y {deleteType.total_area?.toFixed(2)} m² de datos.
            </p>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onClose(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={onConfirm} data-testid="factory-delete-confirm-btn">
              <Trash2 className="w-4 h-4 mr-2" />
              Eliminar Tipo
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Factory Duplicate Modal
 * Modal for duplicating an existing factory cost type with a new name
 */
export const FactoryDuplicateModal = ({
  isOpen,
  onClose,
  duplicateType,
  duplicateName,
  setDuplicateName,
  onConfirm
}) => {
  if (!duplicateType) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="w-5 h-5" />
            Duplicar Tipo
          </DialogTitle>
          <DialogDescription>
            Crea una copia del tipo seleccionado con un nuevo nombre
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-sm text-slate-600">
              Original: <strong>{duplicateType.name}</strong>
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {duplicateType.material} • {duplicateType.entries_count} muestras • ${duplicateType.average_cost_per_m2?.toFixed(2)}/m²
            </p>
          </div>

          <div>
            <Label>Nombre del nuevo tipo</Label>
            <Input
              data-testid="factory-duplicate-name-input"
              value={duplicateName}
              onChange={(e) => setDuplicateName(e.target.value)}
              placeholder="Nombre de la copia"
              className="mt-1"
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onClose(false)}>
              Cancelar
            </Button>
            <Button onClick={onConfirm} disabled={!duplicateName} data-testid="factory-duplicate-confirm-btn">
              <Copy className="w-4 h-4 mr-2" />
              Crear Copia
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
